
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useDiscountSettings } from '@/hooks/useParameters';
import { getDiscountSelectFields } from '@/utils/discountQueryUtils';

export type BusinessDiscount = {
  id: string;
  business_id: string;
  discount_amount: number;
  is_percentage: boolean;
  applies_to_all: boolean;
  is_active: boolean;
  status: 'Pending Validation' | 'Published' | 'Rejected';
  type: 'Paid' | 'Free';
  description: string | null;
  coupon_code: string | null;
  created_at: string;
  updated_at: string;
};

export const useBusinessDiscount = (businessId?: string) => {
  const { shouldDisplayDiscountCode } = useDiscountSettings();
  
  return useQuery({
    queryKey: ['business-discount', businessId, shouldDisplayDiscountCode],
    queryFn: async () => {
      if (!businessId) return null;
      
      const selectFields = getDiscountSelectFields(shouldDisplayDiscountCode);
      
      // Query for the active business discount (is_active = true and status = 'Published')
      const { data, error } = await supabase
        .from('business_discounts')
        .select(selectFields)
        .eq('business_id', businessId)
        .eq('is_active', true)
        .eq('status', 'Published')
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching business discount:', error);
        throw new Error(error.message);
      }
      
      return data as BusinessDiscount | null;
    },
    enabled: !!businessId,
  });
};

// Hook for getting any discount (including pending ones) for business management
export const useBusinessDiscountForManagement = (businessId?: string) => {
  const { shouldDisplayDiscountCode } = useDiscountSettings();
  
  return useQuery({
    queryKey: ['business-discount-management', businessId, shouldDisplayDiscountCode],
    queryFn: async () => {
      if (!businessId) return null;
      
      const selectFields = getDiscountSelectFields(shouldDisplayDiscountCode);
      
      // Get the most recent discount for management purposes
      const { data, error } = await supabase
        .from('business_discounts')
        .select(selectFields)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching business discount for management:', error);
        throw new Error(error.message);
      }
      
      return data as BusinessDiscount | null;
    },
    enabled: !!businessId,
  });
};
